import { Chip, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';

/**
 * Chip d'insight contextuel — affiché dans la cellule "Immat" sous le RegPlate.
 *
 * Contrairement aux chips précédents qui répétaient le nom du statut
 * (déjà donné par le titre du bloc parent), celui-ci affiche UNE info
 * utile à l'agence : nombre de pièces restantes, score IA, montant à
 * régler, ETA des démarches, etc.
 *
 * Convention emoji :
 *   ✨ — signal "IA / Skipcar review" (la marque visuelle attendue)
 *   💳 — paiement attendu côté agence
 *   📮 — envoyé à l'ANTS, en attente du retour
 *   ⛔ — rejet définitif
 *   ⚠️ — double action (paiement + pièces)
 */

interface CGStatusInsightChipProps {
  cg: any;
}

const chipBaseSx = {
  display: 'inline-flex',
  mt: 0.5,
  height: 24,
  fontSize: 12,
  fontWeight: 700,
  borderRadius: '12px',
  letterSpacing: '0.01em',
  '& .MuiChip-label': {
    px: 1.1,
    py: 0,
    fontSize: 12,
    fontWeight: 700,
  },
  cursor: 'pointer',
  transition: 'transform 120ms ease, box-shadow 120ms ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
  },
};

const chipBaseSxStatic = {
  ...chipBaseSx,
  cursor: 'default',
  '&:hover': {
    transform: 'none',
    boxShadow: 'none',
  },
};

export const CGStatusInsightChip = ({ cg }: CGStatusInsightChipProps) => {
  const navigate = useNavigate();
  const status: string = cg.CGReviewStatus ?? 'none';
  const stage: number | null = cg.StageID ?? null;
  const taxTotale: number | undefined = cg.TaxDataEstim?.taxeTotale;
  const ai: number | null = typeof cg.AIConfidence === 'number' ? cg.AIConfidence : null;
  const docTotal: number = Number(cg.DocRequestsTotal ?? 0);
  const docDone: number = Number(cg.DocRequestsFulfilled ?? 0);
  const docOpen: number = Math.max(0, docTotal - docDone);
  // Origine des demandes encore ouvertes : réclamées par l'équipe CG en review,
  // ou issues de la détection à la soumission. Les deux peuvent coexister sur
  // un même dossier.
  const openBySkipcar: number = Number(cg.DocRequestsOpenBySkipcar ?? 0);
  const openByAnalysis: number = Number(cg.DocRequestsOpenByAnalysis ?? 0);

  // ── 0. Réponse attendue (cg_status awaiting_reply) ──
  // Skipcar a écrit sans réclamer de pièce : l'agence doit répondre dans le
  // fil, pas déposer un fichier. Testé avant les autres branches parce que le
  // `cg_review_status` legacy reste à `pending_god_review` ou `awaiting_docs`
  // dans ce cas et afficherait un chip trompeur (« Relue par Skipcar », ou un
  // décompte de pièces à redéposer qui n'existent pas).
  if (cg.cg_status === 'awaiting_reply') {
    const dealId = cg.deal_id ?? cg.DealID ?? cg.RegistrationID;
    return (
      <Tooltip title="Skipcar vous a écrit et attend votre réponse. Cliquez pour ouvrir la discussion.">
        <Chip
          label="💬 Réponse attendue"
          size="small"
          color="warning"
          sx={chipBaseSx}
          onClick={() => navigate(`/list-cg?openExternalComments=${dealId}`)}
        />
      </Tooltip>
    );
  }

  // ── 1. En review Skipcar (pending_god_review) ──
  if (status === 'pending_god_review') {
    // Le score AIConfidence reflète la complétude/qualité du dossier
    // détectée par l'IA. Plus parlant pour l'agence sous l'angle
    // "dossier complet à X%" que "IA X%".
    // Statique (non cliquable) : aucune action attendue côté agence.
    // /cg/:id/review est god-only et redirigeait vers / pour l'agence.
    const label = ai ? `✨ Dossier complet à ${ai}%` : '✨ Relue par Skipcar';
    return (
      <Tooltip title="Notre équipe relit votre dossier avec l'aide de l'IA. Aucune action requise.">
        <Chip label={label} size="small" color="info" sx={chipBaseSxStatic} />
      </Tooltip>
    );
  }

  // ── 2. Pièces à déposer (awaiting_docs) ──
  if (status === 'awaiting_docs') {
    // Compteur explicite au lieu d'un pourcentage trompeur : "0% complet"
    // pour 0/3 redéposées laissait penser que le dossier global était à 0%
    // alors que 6 pièces sur 9 sont déjà validées côté Skipcar. On dit
    // simplement combien sur combien doivent encore être redéposées.
    let label: string;
    if (docTotal <= 1) {
      label = '✨ 1 pièce manquante';
    } else if (docDone === 0) {
      label = `✨ ${docOpen} pièces à redéposer`;
    } else {
      // Progression intra-redepôt (combien sur les demandées ont été renvoyées).
      label = `✨ ${docDone}/${docTotal} redéposées`;
    }
    // Qui réclame : « demandé par Skipcar » quand l'équipe CG a coché la pièce en
    // review, « détecté au dépôt » quand c'est l'analyse. Sur un dossier mixte
    // on ne met rien plutôt qu'une origine fausse.
    const origin =
      openBySkipcar > 0 && openByAnalysis === 0
        ? ' · demandé par Skipcar'
        : openByAnalysis > 0 && openBySkipcar === 0
          ? ' · détecté au dépôt'
          : '';

    return (
      <Tooltip title="Skipcar a demandé un redepôt — cliquez pour les voir et les renvoyer.">
        <Chip
          label={`${label}${origin}`}
          size="small"
          color="warning"
          onClick={() => navigate(`/form-cgia?registrationId=${cg.RegistrationID}`)}
          sx={chipBaseSx}
        />
      </Tooltip>
    );
  }

  // ── 3. Paiement + pièces (awaiting_payment_and_docs) ──
  if (status === 'awaiting_payment_and_docs') {
    const label = `⚠️ ${docOpen || 1} pièce${docOpen > 1 ? 's' : ''} + paiement`;
    return (
      <Tooltip title="Deux actions attendues : régler la facture et redéposer les pièces signalées.">
        <Chip
          label={label}
          size="small"
          color="warning"
          onClick={() => navigate(`/form-cgia?registrationId=${cg.RegistrationID}`)}
          sx={chipBaseSx}
        />
      </Tooltip>
    );
  }

  // ── 4. Paiement seul (awaiting_payment, post-fulfill One-time) ──
  if (status === 'awaiting_payment') {
    const label =
      taxTotale != null && taxTotale > 0
        ? `💳 ${taxTotale.toFixed(2)} € à régler`
        : '💳 Paiement attendu';
    return (
      <Tooltip title="Vos pièces sont validées. Réglez la facture pour lancer les démarches.">
        <Chip
          label={label}
          size="small"
          color="info"
          sx={chipBaseSxStatic}
        />
      </Tooltip>
    );
  }

  // ── 5. Démarches en cours (god_approved, hors Stage 48) ──
  if (status === 'god_approved' && stage !== 48) {
    return (
      <Tooltip title="Votre dossier est à l'ANTS, en attente du retour officiel.">
        <Chip
          label="📮 Démarches ANTS en cours"
          size="small"
          color="info"
          sx={chipBaseSxStatic}
        />
      </Tooltip>
    );
  }

  // Pas de chip pour les états neutres (none, completed, god_rejected).
  // Le titre du bloc parent ("Procédure effectuée", "Rejetées", etc.) donne
  // déjà toute l'info — un chip "⛔ Voir le motif" sans modale de détail
  // derrière n'apporte rien d'actionnable.
  return null;
};
